import { useEffect, useMemo, useState, Fragment } from "react";
import { useTheme } from "../../../common/ThemeContext";
import { useSeason } from "../../../common/SeasonContext";
import { usePlayersPerf } from "../../../common/PlayersPerfContext";
import { filterEnabledPlayers } from "../../../common/playerFilter";
import {
  hasLoadSimulationData,
  isPlayerAtRisk,
  PLAYER_AT_RISK_TRAINABILITY_THRESHOLD,
  TEAM_OVERVIEW_RISK_MODE_AT_RISK,
} from "../../../common/playerRiskUtils.js";
import { usePdfExport } from "../../../rapport/pdf/PdfExportContext";
import ChartCapture from "../../pdf/ChartCapture";
import { fetchAuth } from "../../../common/api";
import { ComposedChart, Area, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend, ResponsiveContainer, ReferenceArea } from "recharts";
import { Search, Filter, Users, Eye, EyeOff } from "lucide-react";

const METRICS = {
  internal: [
    { key: "trainability", label: "Trainability (index)", unit: "" },
    { key: "mechanical_fatigue", label: "Mechanical Fatigue (%)",
     // tolère weeklyHistory.fatigue ou weeklyHistory.mechanical_fatigue
     get: (s) => s.mechanical_fatigue ?? s.fatigue ?? null,
     // formattage “smart”: si >1 on considère déjà des %, sinon fraction
     fmt: (v) => {
       if (!Number.isFinite(v)) return null;
       const pct = v > 1.5 ? v : v * 100; // 29.7 => %; 0.297 => 29.7%
       return +pct.toFixed(1);
     }
   },
    { key: "intensity_index",        label: "Intensity (PT)",                         fmt: (v)=> Number.isFinite(v) ? +v.toFixed(2) : null },
    { key: "mechanical_load_index",  label: "Mechanical Load (PT)",                   fmt: (v)=> Number.isFinite(v) ? +v.toFixed(2) : null },
    { key: "power",                  label: "Max horizontal mechanical power (W/kg)", fmt: (v)=> Number.isFinite(v) ? +v.toFixed(2) : null },
  ],
  external: [
    { key: "accelerations",   label: "Accelerations max (m/s²)", fmt: (v)=> Number.isFinite(v) ? +v.toFixed(2) : null },
    { key: "decelerations",   label: "Decelerations max (m/s²)", fmt: (v)=> Number.isFinite(v) ? +v.toFixed(2) : null },
    { key: "sprint_max",      label: "Speed max (km/h)",         fmt: (v)=> Number.isFinite(v) ? +v.toFixed(2) : null },
    { key: "global_distance", label: "Total Distance (km)",      fmt: (v)=> Number.isFinite(v) ? +v.toFixed(2) : null },
  ],
};

const COLORS = [
  "#3B82F6","#22C55E","#EF4444","#F59E0B","#8B5CF6",
  "#EC4899","#10B981","#06B6D4","#84CC16","#F97316",
];

function readTrendMetricValue(sample, metric) {
  if (!sample || !metric) return null;
  if (typeof metric.get === "function") return metric.get(sample);
  return sample[metric.key];
}

function hasWeeklyTrendSimulationData(player) {
  if (hasLoadSimulationData(player)) return true;

  const history = Array.isArray(player?.weeklyHistory) ? player.weeklyHistory : [];
  const metrics = [...METRICS.internal, ...METRICS.external];

  return history.some((sample) =>
    metrics.some((metric) => Number.isFinite(Number(readTrendMetricValue(sample, metric))))
  );
}

function PlayerPill({ checked, label, onToggle }) {
  const base =
    "px-2 py-1 rounded-lg border text-xs transition-colors " +
    "focus:outline-none focus:ring-2 focus:ring-indigo-500/40";

  const onCls =
    "bg-indigo-600 text-white border-indigo-600 " +
    "dark:bg-indigo-500 dark:border-indigo-500";

  // lighter (more readable) when unchecked in dark mode
  const offCls =
    "bg-white text-gray-700 border-gray-300 hover:bg-gray-50 " +
    "dark:bg-gray-700 dark:text-gray-100 dark:border-gray-500 dark:hover:bg-gray-600";

  return (
    <button
      onClick={onToggle}
      className={`${base} ${checked ? onCls : offCls}`}
      title={label}
      aria-pressed={checked}
    >
      {checked ? (
        <Eye className="inline mr-1 h-3 w-3" />
      ) : (
        <EyeOff className="inline mr-1 h-3 w-3" />
      )}
      {label}
    </button>
  );
}


function OutlierDot({ cx, cy, value, payload, color }) {
  // value = valeur du joueur à cette date
  const lo = payload?.__band_lo;
  const hi = payload?.__band_hi;
  if (!Number.isFinite(value) || !Number.isFinite(lo) || !Number.isFinite(hi)) return null;
  const isOut = value < lo || value > hi;
  if (!isOut) return null;

  const r = 4; // rayon du dot d’alerte
  return (
    <g>
      <circle cx={cx} cy={cy} r={r+2} fill="white" stroke={color} strokeWidth={2} />
      <circle cx={cx} cy={cy} r={r} fill={color} />
    </g>
  );
}

function tooltipTheme(dark) {
  return {
    contentStyle: {
      fontSize: 12,
      backgroundColor: dark ? "#111827" : "#ffffff",            // gray-900 / white
      border: `1px solid ${dark ? "#374151" : "#E5E7EB"}`,       // gray-700 / gray-200
      color: dark ? "#F3F4F6" : "#111827",                       // gray-100 / gray-900
      borderRadius: 8,
      boxShadow: dark ? "0 6px 16px rgba(0,0,0,0.45)" : "0 6px 16px rgba(0,0,0,0.15)",
      padding: "8px 10px",
    },
    labelStyle: { color: dark ? "#D1D5DB" : "#111827", marginBottom: 4 },
    itemStyle:  { color: dark ? "#F9FAFB" : "#111827" },
  };
}

function TrendsTooltip({ active, label, payload, dark }) {
  if (!active || !payload || !payload.length) return null;
  const theme = tooltipTheme(dark);

  // On masque la zone de bande ±σ (__band) dans le tooltip pour éviter le bruit
  const items = payload.filter(it => it && it.dataKey !== "__band");

  return (
    <div style={theme.contentStyle}>
      {/* Date / label */}
      <div style={{ ...theme.labelStyle, fontWeight: 600 }}>{label}</div>

      {/* Lignes: pastille (même couleur que la courbe) / nom / valeur */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "12px 1fr auto",
          gap: 8,
          alignItems: "center",
          marginTop: 2,
        }}
      >
        {items.map((it) => {
          const isMedian = it.dataKey === "__median";
          const color = isMedian ? (dark ? "#E5E7EB" : "#111827") : (it.color || "#8884d8");
          const name = isMedian ? "Median" : (it.name ?? it.dataKey);
          const value = Number.isFinite(it.value) ? it.value.toFixed(2) : "—";

          return (
            <Fragment key={`${it.dataKey}-${name}`}>
              {/* pastille/segment couleur, comme la courbe */}
              <span
                aria-hidden
                style={{
                  width: 12,
                  height: 3,
                  background: color,
                  borderRadius: 2,
                  display: "inline-block",
                }}
              />
              <span style={theme.itemStyle}>{name}</span>
              <span
                style={{
                  ...theme.itemStyle,
                  fontVariantNumeric: "tabular-nums",
                  marginLeft: 12,
                }}
              >
                {value}
              </span>
            </Fragment>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Props :
 * - selectedDate: string (ex: most_recent_simulation) — requis
 * - selectedPosition?: string (conservé pour compat, mais le multi-postes interne prime)
 * - defaultCategory? (supprimé : on a maintenant 2 toggles de visibilité)
 */
export default function ClubTrendsPanel({
  selectedDate,
  selectedPosition = "All",
  riskMode,
  activeAlertPlayerIds,
}) {
  const exporter = usePdfExport();
  const { isDarkMode } = useTheme();
  const { season } = useSeason();
  const { players: enabledPlayers } = usePlayersPerf();

  const [loading, setLoading] = useState(true);
  const [weeklyData, setWeeklyData] = useState([]);
  const [playersFiltered, setPlayersFiltered] = useState([]);

  // --- UI state
  const [search, setSearch] = useState("");
  const [groupVisibility, setGroupVisibility] = useState({ internal: true, external: true }); // ✅ 2 groupes actifs par défaut
  const [showTeamAvg, setShowTeamAvg] = useState(true);
  const [period, setPeriod] = useState("week"); // "week" | "month" | "season"
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");

  const hasValidCustomRange = useMemo(() => {
    if (!customStart || !customEnd) return false;
    const s = new Date(customStart);
    const e = new Date(customEnd);
    return isFinite(+s) && isFinite(+e) && s <= e;
  }, [customStart, customEnd]);

  // Visibilité des graphes (par métrique)
  const allMetricKeys = useMemo(() => [...METRICS.internal, ...METRICS.external].map(m=>m.key), []);
  const [chartVisibility, setChartVisibility] = useState(() => {
    const o = {};
    allMetricKeys.forEach(k => o[k] = true);
    return o;
  });

  const [visiblePlayers, setVisiblePlayers] = useState({});

  const KNOWN_POSITIONS = useMemo(() => {
    const set = new Set(weeklyData.map(p => p.position || "N/A"));
    const ordered = ["Defense","Midfield","Forward","Lateral","N/A"].filter(x => set.has(x));
    [...set].forEach(x => { if (!ordered.includes(x)) ordered.push(x); });
    return ordered;
  }, [weeklyData]);

  const [selectedPositions, setSelectedPositions] = useState(() => {
    if (selectedPosition && selectedPosition !== "All") return new Set([selectedPosition]);
    return new Set(KNOWN_POSITIONS);
  });

  const eligiblePlayerById = useMemo(() => {
    const basePlayers = filterEnabledPlayers(
      Array.isArray(enabledPlayers) ? enabledPlayers : []
    );

    const scopedPlayers =
      riskMode === TEAM_OVERVIEW_RISK_MODE_AT_RISK
        ? basePlayers.filter((player) =>
          isPlayerAtRisk(player, {
            activeAlertPlayerIds,
            trainabilityThreshold: PLAYER_AT_RISK_TRAINABILITY_THRESHOLD,
          })
        )
        : basePlayers;

    return new Map(scopedPlayers.map((player) => [player.id, player]));
  }, [enabledPlayers, riskMode, activeAlertPlayerIds]);
  
  useEffect(() => {
   if (period !== "custom") {
     setCustomStart("");
     setCustomEnd("");
   }
 }, [period]);

  useEffect(() => {
    // En mode custom, attendre un intervalle valide avant de fetch
    if (period === "custom" && !hasValidCustomRange) {
      setLoading(false);
      return; // on ne fetch pas, on ne vide pas
    }

    if (!selectedDate && period !== "custom") {
      setWeeklyData([]);
      setPlayersFiltered([]);
      setLoading(false);
      return;
    }
    (async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        params.set("season", season);
        if (period === "custom") {
          params.set("period", "custom");
          params.set("start_date", customStart); // yyyy-mm-dd
          params.set("end_date", customEnd);
        } else {
          params.set("date", selectedDate);
          params.set("period", period);
        }
        const res = await fetchAuth(`/players_weekly_history?${params.toString()}`);
        if (!res.ok) throw new Error(`players_weekly_history ${res.status}`);
        const data = await res.json();

        // Filter out disabled players
        const allData = Array.isArray(data) ? data : [];
        const filteredData = allData.filter((p) => {
          const contextPlayer = eligiblePlayerById.get(p.id);
          if (!contextPlayer) return false;
          return hasWeeklyTrendSimulationData(p) || hasLoadSimulationData(contextPlayer);
        });

      
        setWeeklyData(filteredData);
      } catch (e) {
   
      } finally {
        setLoading(false);
      }
    })();
  }, [selectedDate, season, period, customStart, customEnd, hasValidCustomRange, eligiblePlayerById]);

  // Aligne selectedPositions quand les positions connues arrivent
  useEffect(() => {
    if (selectedPositions.size === 0 && KNOWN_POSITIONS.length) {
      setSelectedPositions(new Set(KNOWN_POSITIONS));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [KNOWN_POSITIONS.join("|")]);

  // Parse la recherche : noms/texte + postes multiples (space/virgule)
  const parsedSearchPositions = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return new Set();
    const tokens = q.split(/[,\s]+/).filter(Boolean);
    const posSet = new Set();
    tokens.forEach(t => {
      KNOWN_POSITIONS.forEach(p => {
        if (p && p.toLowerCase().startsWith(t)) posSet.add(p);
      });
    });
    return posSet;
  }, [search, KNOWN_POSITIONS]);

  // Filtrage joueurs (multi-postes + recherche)
  useEffect(() => {
    // base : positions
    const posAllowed = parsedSearchPositions.size
      ? parsedSearchPositions // la recherche impose un sous-ensemble de postes
      : selectedPositions.size ? selectedPositions : new Set(KNOWN_POSITIONS);

    const inPositions = (p) => posAllowed.has((p.position || "N/A"));

    // filtre noms / texte libre (si tokens non position)
    const q = search.trim().toLowerCase();
    const hasText = q && [...parsedSearchPositions].length === 0;
    const matchText = (p) => `${p.first_name} ${p.last_name}`.toLowerCase().includes(q) || (p.position||"").toLowerCase().includes(q);

    const filtered = weeklyData.filter(p => inPositions(p) && (!hasText || matchText(p)));
    setPlayersFiltered(filtered);

    // init/sync visibilité joueurs
    setVisiblePlayers(prev => {
      const copy = { ...prev };
      filtered.forEach(p => {
        const name = `${p.first_name} ${p.last_name}`;
        if (!(name in copy)) copy[name] = true; // visible par défaut
      });
      return copy;
    });
  }, [weeklyData, selectedPositions, KNOWN_POSITIONS, search, parsedSearchPositions]);

  // Construit les séries (une par métrique)
  const seriesPerMetric = useMemo(() => {
    if (!playersFiltered.length) return {};

    const uniqueDates = [...new Set(
      playersFiltered.flatMap(p => (p.weeklyHistory || []).map(s => s.day))
    )].sort((a, b) => {
      const da = new Date(a.split("/").reverse().join("-"));
      const db = new Date(b.split("/").reverse().join("-"));
      return da - db;
    });

    const out = {};
    const allDefs = [...METRICS.internal, ...METRICS.external];

    // helpers
    const median = (arr) => {
      const a = arr.slice().sort((x,y)=>x-y);
      const n = a.length;
      if (!n) return null;
      const mid = Math.floor(n/2);
      return n%2 ? a[mid] : (a[mid-1]+a[mid])/2;
    };

    for (const m of allDefs) {
      const rows = uniqueDates.map(d => {
        const row = { date: d };
        const valsForStats = [];

        playersFiltered.forEach(p => {
          const name = `${p.first_name} ${p.last_name}`;
          const session = (p.weeklyHistory || []).find(s => s.day === d);
          const rawVal = session
            ? (typeof m.get === "function" ? m.get(session) : session[m.key])
            : null;
          const val = m.fmt ? m.fmt(rawVal) : rawVal;
          row[name] = (Number.isFinite(val) ? val : null);
          if (Number.isFinite(val)) valsForStats.push(val);
        });

        if (valsForStats.length) {
          const mu = valsForStats.reduce((a,b)=>a+b,0) / valsForStats.length;
          const sd = Math.sqrt(
            valsForStats.reduce((s, x)=> s + (x-mu)*(x-mu), 0) / valsForStats.length
          );
          row.__mu = +mu.toFixed(2);
          row.__sd = +sd.toFixed(2);
          row.__band_lo = +(mu - sd).toFixed(2);
          row.__band_hi = +(mu + sd).toFixed(2);
          row.__median = +median(valsForStats).toFixed(2);
          // format "range" pour Area range (si supporté)
          row.__band = [row.__band_lo, row.__band_hi];
        } else {
          row.__mu = row.__sd = row.__band_lo = row.__band_hi = row.__median = null;
          row.__band = [null, null];
        }

        return row;
      });

      out[m.key] = rows;
    }
    return out;
  }, [playersFiltered]);

  const allVisible = useMemo(() => {
    const names = playersFiltered.map(p => `${p.first_name} ${p.last_name}`);
    return names.length && names.every(n => visiblePlayers[n]);
  }, [playersFiltered, visiblePlayers]);

  const setAllPlayers = (val) => {
    setVisiblePlayers(prev => {
      const copy = { ...prev };
      playersFiltered.forEach(p => {
        const name = `${p.first_name} ${p.last_name}`;
        copy[name] = val;
      });
      return copy;
    });
  };


  const toggleChart = (key) => setChartVisibility(v => ({ ...v, [key]: !v[key] }));
  const toggleGroup = (cat) => setGroupVisibility(g => ({ ...g, [cat]: !g[cat] }));
  const togglePosition = (pos) =>
    setSelectedPositions(prev => {
      const next = new Set(prev);
      if (next.has(pos)) next.delete(pos);
      else next.add(pos);
      return next;
    });

  const colors = COLORS;
  const visibleNames = Object.entries(visiblePlayers).filter(([,v])=>v).map(([name])=>name);

  const visibleInternalKeys = METRICS.internal
    .filter(m => chartVisibility[m.key])
    .map(m => `club.internal.${m.key}`);

  const visibleExternalKeys = METRICS.external
    .filter(m => chartVisibility[m.key])
    .map(m => `club.external.${m.key}`);

  useEffect(() => {
    const colorMap = {};
    visibleNames.forEach((name, i) => {
      colorMap[name] = COLORS[i % COLORS.length];
    });
    exporter.setData("__legend", {
      colorMap,
      showTeamAvg,
      teamAvgColor: "#111827"
    });
  }, [exporter, visibleNames.join("|"), showTeamAvg]);

  useEffect(() => {
    exporter.setData("__series", {}); 
    Object.entries(seriesPerMetric).forEach(([metricKey, rows]) => {
      // rows = [{date, Player A, Player B, __avg}, ...]
      exporter.setData("__series", { [metricKey]: rows });
    });
  }, [seriesPerMetric]);

  // publie ces infos à chaque render (léger & idempotent)
  useEffect(() => {
    exporter.setData("__club_internal_keys", visibleInternalKeys);
    exporter.setData("__club_external_keys", visibleExternalKeys);
  }, [exporter, visibleInternalKeys.join("|"), visibleExternalKeys.join("|")]);

  return (
    <div className={`rounded-2xl border ${isDarkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"} p-5`}>
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <span className="text-sm opacity-70 flex items-center gap-2">
          <Filter className="h-4 w-4" />
          {period === "custom"
            ? (hasValidCustomRange
                ? `Trends (${customStart} → ${customEnd})`
                : "Trends (custom — select dates)")
            : `Trends (${period})`}
        </span>


        {/* Period selector */}
        <div className="ml-auto flex items-center gap-2">
          <label className={`${isDarkMode ? "text-gray-300" : "text-gray-700"} text-sm`}>Period</label>
          <select
            value={period}
            onChange={(e)=>setPeriod(e.target.value)}
            className={`px-2 py-1 rounded border text-sm ${
              isDarkMode ? "bg-gray-700 border-gray-600 text-gray-100" : "bg-white border-gray-300"
            }`}
          >
            <option value="week">Week</option>
            <option value="month">Month</option>
            <option value="season">Season</option>
            <option value="custom">Custom</option>
          </select>

          {period === "custom" && (
            <div className={`flex items-center gap-2 px-2 py-1 rounded border ${isDarkMode ? "bg-gray-700 border-gray-600" : "bg-gray-50 border-gray-300"}`}>
              <input
                type="date"
                value={customStart}
                onChange={(e)=>setCustomStart(e.target.value)}
                max={customEnd || undefined}
                className={`px-2 py-1 rounded border text-sm ${isDarkMode ? "bg-gray-800 border-gray-600 text-gray-100" : "bg-white border-gray-300"}`}
                aria-label="Start date"
              />
              <span className="opacity-60">→</span>
              <input
                type="date"
                value={customEnd}
                onChange={(e)=>setCustomEnd(e.target.value)}
                min={customStart || undefined}
                className={`px-2 py-1 rounded border text-sm ${isDarkMode ? "bg-gray-800 border-gray-600 text-gray-100" : "bg-white border-gray-300"}`}
                aria-label="End date"
              />
            </div>
          )}
        </div>

        <div className="inline-flex rounded-lg overflow-hidden border dark:border-gray-600">
          {["internal","external"].map(cat => {
            const active = groupVisibility[cat];
            return (
              <button
                key={cat}
                onClick={()=>toggleGroup(cat)}
                className={`px-3 py-1.5 text-sm ${
                  active
                    ? "bg-indigo-600 text-white"
                    : isDarkMode ? "bg-gray-700 text-gray-300" : "bg-gray-100 text-gray-500"
                }`}
                title={active ? `Hide ${cat}` : `Show ${cat}`}
              >
                {cat === "internal" ? "Internal" : "External"}
              </button>
            );
          })}
        </div>

        {/* Recherche texte + postes multiples */}
        <div className={`flex items-center gap-2 px-2 py-1 rounded border ${isDarkMode ? "bg-gray-700 border-gray-600" : "bg-gray-50 border-gray-300"}`}>
          <Search className="h-4 w-4 opacity-70" />
          <input
            value={search}
            onChange={(e)=>setSearch(e.target.value)}
            className={`bg-transparent outline-none text-sm ${isDarkMode ? "placeholder-gray-400 text-gray-100" : "placeholder-gray-500 text-gray-900"}`}
            placeholder="Search players… or positions (e.g. defense, forward)"
          />
        </div>

        <button
          onClick={()=>setAllPlayers(!allVisible)}
          className={`px-3 py-1.5 text-sm rounded border inline-flex items-center gap-2
            ${isDarkMode ? "bg-gray-700 border-gray-600" : "bg-gray-50 border-gray-300"}`}
          title={allVisible ? "Hide all players" : "Show all players"}
        >
          <Users className="h-4 w-4" /> {allVisible ? "Hide all" : "Show all"}
        </button>
      </div>

      {KNOWN_POSITIONS.length > 0 && (
        <div className="mb-3 flex flex-wrap gap-2">
          {KNOWN_POSITIONS.map(pos => {
            const on = selectedPositions.has(pos);
            return (
              <button
                key={pos}
                onClick={()=>togglePosition(pos)}
                className={`px-2 py-1 rounded-lg border text-xs transition ${
                  on
                    ? "bg-emerald-600 text-white border-emerald-600"
                    : isDarkMode ? "bg-gray-700 text-gray-200 border-gray-600" : "bg-gray-100 text-gray-800 border-gray-300"
                }`}
              >
                {pos}
              </button>
            );
          })}
        </div>
      )}

      <div className="mb-4 flex gap-1 flex-wrap">
        {playersFiltered.map((p) => {
          const name = `${p.first_name} ${p.last_name}`;
          const checked = !!visiblePlayers[name];
          return (
            <PlayerPill
              key={name}
              checked={checked}
              label={name}
              onToggle={()=> setVisiblePlayers(v => ({...v, [name]: !v[name]}))}
            />
          );
        })}
      </div>

      <div className="mb-4 space-y-3">
        <div>
          <div className={`${isDarkMode ? "text-gray-300" : "text-gray-700"} text-sm font-semibold mb-1`}>
            Charts visibility — Internal
          </div>
          <div className="flex flex-wrap gap-2">
            {METRICS.internal.map(m => (
              <label
                key={m.key}
                className={`px-2 py-1 rounded border text-xs cursor-pointer ${
                  chartVisibility[m.key]
                    ? "bg-indigo-600 text-white border-indigo-600"
                    : isDarkMode ? "bg-gray-700 text-gray-200 border-gray-600" : "bg-gray-100 text-gray-800 border-gray-300"
                }`}
                onClick={()=>toggleChart(m.key)}
              >
                {m.label}
              </label>
            ))}
          </div>
        </div>
        <div>
          <div className={`${isDarkMode ? "text-gray-300" : "text-gray-700"} text-sm font-semibold mb-1`}>
            Charts visibility — External
          </div>
          <div className="flex flex-wrap gap-2">
            {METRICS.external.map(m => (
              <label
                key={m.key}
                className={`px-2 py-1 rounded border text-xs cursor-pointer ${
                  chartVisibility[m.key]
                    ? "bg-indigo-600 text-white border-indigo-600"
                    : isDarkMode ? "bg-gray-700 text-gray-200 border-gray-600" : "bg-gray-100 text-gray-800 border-gray-300"
                }`}
                onClick={()=>toggleChart(m.key)}
              >
                {m.label}
              </label>
            ))}
          </div>
        </div>

        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={showTeamAvg} onChange={e=>setShowTeamAvg(e.target.checked)} />
          <span>Show team average</span>
        </label>
      </div>

      {!playersFiltered.length ? (
        loading
          ? <p className="text-sm opacity-70">Loading season trends…</p>
          : <p className="text-sm opacity-70">No time-series available for this selection.</p>
      ) : (
        <div className="relative">
          {loading && (
            <div className="absolute top-0 right-0 text-xs px-2 py-1 m-2 rounded bg-indigo-600 text-white">
              Updating…
            </div>
          )}
          <div className="space-y-8">
            {/* ——— INTERNAL ——— */}
            {groupVisibility.internal && (
              <>
                <SectionTitle dark={isDarkMode}>Internal</SectionTitle>
                {METRICS.internal.map((m) => {
                  if (!chartVisibility[m.key]) return null;
                  const data = seriesPerMetric[m.key] || [];
                  return (
                    <ChartCard
                      key={m.key}
                      title={m.label}
                      data={data}
                      showTeamAvg={showTeamAvg}
                      visibleNames={visibleNames}
                      colors={colors}
                      imageKey={`club.internal.${m.key}`}
                      dark={isDarkMode}
                    />
                  );
                })}
              </>
            )}

            {groupVisibility.internal && groupVisibility.external && (
              <hr className={isDarkMode ? "border-gray-700" : "border-gray-200"} />
            )}

            {/* ——— EXTERNAL ——— */}
            {groupVisibility.external && (
              <>
                <SectionTitle dark={isDarkMode}>External</SectionTitle>
                {METRICS.external.map((m) => {
                  if (!chartVisibility[m.key]) return null;
                  const data = seriesPerMetric[m.key] || [];
                  return (
                    <ChartCard
                      key={m.key}
                      title={m.label}
                      data={data}
                      showTeamAvg={showTeamAvg}
                      visibleNames={visibleNames}
                      colors={colors}
                      imageKey={`club.external.${m.key}`}
                      dark={isDarkMode}
                    />
                  );
                })}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function SectionTitle({ children, dark }) {
  return (
    <div className={`text-sm font-semibold ${dark ? "text-gray-200" : "text-gray-700"}`}>
      {children}
    </div>
  );
}

function ChartCard({ title, data, showTeamAvg, visibleNames, colors, imageKey, dark }) {
  // On n’affiche plus la moyenne; on garde la médiane
  // Plage ±σ : on tente Area "range" si supporté, sinon fallback rectangles par pas

  const useAreaRange = true; // laisse à true; Recharts récent gère [lo,hi] en dataKey

  return (
    <div className="rounded-xl border p-3 dark:border-gray-700 border-gray-200">
      <ChartCapture imageKey={imageKey}>
      <div className="text-sm font-semibold mb-2">{title}</div>
      <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={data} margin={{ top: 10, right: 20, bottom: 0, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip content={(props) => <TrendsTooltip dark={dark} {...props} />} />
              <Legend wrapperStyle={{ color: dark ? "#E5E7EB" : "#111827", fontSize: 12 }} />

              {/* === BANDE ±σ === */}
              {useAreaRange ? (
                <Area
                  type="monotone"
                  dataKey="__band"           // tableau [lo,hi]
                  name="± 1σ"
                  stroke="none"
                  fill="#6366F1"
                  fillOpacity={0.18}
                  activeDot={false}
                  isAnimationActive={false}
                />
              ) : (
                // Fallback: rectangles entre dates (si jamais ton Recharts ne gère pas les ranges)
                data.map((row, i) => {
                  const next = data[i+1];
                  if (!next || !Number.isFinite(row.__band_lo) || !Number.isFinite(row.__band_hi)) return null;
                  return (
                    <ReferenceArea
                      key={`band-${i}`}
                      x1={row.date}
                      x2={next.date}
                      y1={row.__band_hi}
                      y2={row.__band_lo}
                      strokeOpacity={0}
                      fill="#6366F1"
                      fillOpacity={0.18}
                    />
                  );
                })
              )}

              {/* === MÉDIANE === */}
              <Line
                type="monotone"
                dataKey="__median"
                name="Median"
                stroke={dark ? "#E5E7EB" : "#111827"}
                dot={false}
                strokeWidth={2}
                isAnimationActive={false}
              />

              {/* === COURBES JOUEURS + DOTS OUTLIERS === */}
              {visibleNames.map((name, i) => (
                <Line
                  type="monotone"
                  dataKey={name}
                  name={name}
                  stroke={colors[i % colors.length]}
                  dot={(props) => {
                    const { key: _k, ...rest } = props;
                    return <OutlierDot {...rest} color={colors[i % colors.length]} />;
                  }}
                  activeDot={false}
                  strokeWidth={2}
                  connectNulls={false}
                  isAnimationActive={false}
                />
              ))}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </ChartCapture>
    </div>
    
  );
}
